import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_URL as API } from '../lib/api';


const DEFAULT_THEME = {
  accent_color: '#F2F2F7',
  sidebar_color: '#0a0a0a',
  font_family: 'Inter',
  font_size: 'normal',
  border_radius: 'none',
};

const PrefsContext = createContext(null);

export function usePrefs() {
  return useContext(PrefsContext);
}

export function PrefsProvider({ children }) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPrefs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/settings/preferences`, { withCredentials: true });
      setPrefs(data);
      applyTheme(data.theme || DEFAULT_THEME);
    } catch {
      // Not logged in or error — use defaults
      applyTheme(DEFAULT_THEME);
    }
    setLoading(false);
  }, []);

  const updatePrefs = useCallback(async (section, value) => {
    try {
      const { data } = await axios.put(`${API}/api/settings/preferences`, { [section]: value }, { withCredentials: true });
      setPrefs(data);
      if (section === 'theme') {
        applyTheme(data.theme || DEFAULT_THEME);
      }
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    fetchPrefs();
  }, [fetchPrefs]);

  return (
    <PrefsContext.Provider value={{ prefs, loading, fetchPrefs, updatePrefs }}>
      {children}
    </PrefsContext.Provider>
  );
}

function applyTheme(theme) {
  const root = document.documentElement;
  const t = { ...DEFAULT_THEME, ...theme };

  root.style.setProperty('--accent', t.accent_color);
  root.style.setProperty('--accent-10', t.accent_color + '1a');
  root.style.setProperty('--accent-20', t.accent_color + '33');
  root.style.setProperty('--accent-30', t.accent_color + '4d');
  root.style.setProperty('--sidebar-bg', t.sidebar_color);

  // Font
  const fontMap = {
    'Inter': "'Inter', sans-serif",
    'JetBrains Mono': "'JetBrains Mono', monospace",
    'Space Grotesk': "'Space Grotesk', sans-serif",
    'DM Sans': "'DM Sans', sans-serif",
    'Poppins': "'Poppins', sans-serif",
    'Roboto Mono': "'Roboto Mono', monospace",
  };
  root.style.setProperty('--font-family', fontMap[t.font_family] || fontMap['Inter']);

  // Font size scale
  const sizeMap = { small: '0.9', normal: '1', large: '1.1' };
  root.style.setProperty('--font-scale', sizeMap[t.font_size] || '1');

  // Border radius
  const radiusMap = { none: '0px', small: '4px', medium: '8px', large: '12px' };
  root.style.setProperty('--radius-app', radiusMap[t.border_radius] || '0px');
}
